/**
 * Onboarding chat session contracts (phases 1 and 2: chat foundation, real brand and ads analysis).
 *
 * Route paths + request/response Zod schemas for `/v1/onboarding/sessions/*`,
 * shared by the FE data layer and the BFF routes. Lives in `contracts/` because
 * the BFF Dockerfile copies only that tree. Plan:
 * `Specs/project_onboarding/plans/plan_phase-1-chat-foundation.md`.
 *
 * Conventions:
 *  - The client creates the session id (`PUT /:id` is create-or-attach).
 *  - A skipped or "prefer not to say" answer leaves its field ABSENT; there is
 *    never a default value.
 *  - Step states are explicit; a failed read shows as `unavailable`, no fake ticks.
 */
import { z } from 'zod';

/* ----------------------------- Route paths ------------------------------ */

export const ONBOARDING_SESSIONS_PREFIX = '/v1/onboarding/sessions';

export const ONBOARDING_SESSION_ROUTES = {
  /** PUT `{url}` create-or-attach + start jobs; GET aggregated snapshot. */
  session: '/v1/onboarding/sessions/:id',
  /** POST the `BrandProfileInput` baseline. */
  quiz: '/v1/onboarding/sessions/:id/quiz',
  brandGuide: '/v1/onboarding/sessions/:id/brand-guide',
  /** GET one entry per ad platform the client named. */
  adAccounts: '/v1/onboarding/sessions/:id/ad-accounts',
  /** PUT the confirmed accounts of one platform. */
  adAccountsPlatform: '/v1/onboarding/sessions/:id/ad-accounts/:platform',
  /** GET `?since=` platform-neutral discovery events. */
  adEvents: '/v1/onboarding/sessions/:id/ads/:platform/events',
  /** GET shortlist; PUT confirmed brands. */
  competitors: '/v1/onboarding/sessions/:id/competitors',
  competitorIcon: '/v1/onboarding/sessions/:id/competitors/:brandId/icon',
  /** GET the brand ad profile (analyzed X of Y, mix, patterns) of one platform's target account. */
  adAnalysis: '/v1/onboarding/sessions/:id/ads/:platform/analysis',
} as const;

/* ------------------------------- Limits --------------------------------- */

/** Longest URL / domain the client may type (a browser's practical URL cap). */
export const MAX_URL_LENGTH = 2048;
/** Longest id string (an account id, a brand id) the client may send back. */
export const MAX_ID_LENGTH = 200;
/** Longest short free text (a channel name, a pain, a team size label). */
export const MAX_SHORT_TEXT_LENGTH = 120;
/** Longest long free text (the "other challenge" answer). */
export const MAX_LONG_TEXT_LENGTH = 1000;
/** Most channels / pains one answer set can carry. */
export const MAX_CHANNELS = 20;
export const MAX_PAINS = 20;
/** Most ad accounts one platform confirmation can carry. */
export const MAX_AD_ACCOUNT_IDS = 100;
/** Most competitors that can be confirmed (interaction #16). */
export const MAX_COMPETITOR_PICKS = 3;
/** A competitor's `reason`: one sentence from the live search. */
export const MAX_COMPETITOR_REASON_LENGTH = 300;
/** Upper bound of a USD range value, far past any real brand (rejects junk, not budgets). */
export const MAX_USD_AMOUNT = 1_000_000_000_000;
/** Most advertiser accounts / items / warnings in a list the BFF returns. */
export const MAX_RANKED_ITEMS = 50;
/** Longest value the BFF returns for strings it read off a third-party site (clamped, never rejected). */
export const MAX_SITE_NAME_LENGTH = 200;
export const MAX_SITE_DESCRIPTION_LENGTH = 1000;
export const MAX_SITE_PRICE_LENGTH = 32;
export const MAX_PRODUCT_NAME_LENGTH = 200;
/** Highest `since` cursor a client may send (a bound on junk, far past any real event sequence). */
export const MAX_EVENT_CURSOR = 1_000_000_000;

/* -------------------------------- Ids ----------------------------------- */

/**
 * Client-chosen, created once per browser session. Specifically a UUID v4 (random):
 * the version nibble must be 4 and the variant nibble 8, 9, a or b.
 */
export const SessionIdSchema = z
  .string()
  .uuid()
  .refine((id) => id[14] === '4' && /^[89ab]$/i.test(id[19] ?? ''), { message: 'session id must be a UUID v4' });
export type SessionId = z.infer<typeof SessionIdSchema>;

/* ----------------------------- Ad platforms ----------------------------- */

export const AD_PLATFORMS = [
  'meta',
  'tiktok',
  'google',
  'youtube',
  'pinterest',
  'snapchat',
  'amazon',
] as const;
export const AdPlatformSchema = z.enum(AD_PLATFORMS);
export type AdPlatform = z.infer<typeof AdPlatformSchema>;

/** Quiz channel names that are the same ad platform under another label (lowercase keys). */
export const AD_CHANNEL_ALIASES: Readonly<Record<string, AdPlatform>> = {
  facebook: 'meta',
  instagram: 'meta',
  'meta ads': 'meta',
};

/** The ad platform a quiz channel name stands for, or null when it is not an ad platform (e.g. email). */
export function adPlatformOfChannel(channel: string): AdPlatform | null {
  const key = channel.trim().toLowerCase();
  if ((AD_PLATFORMS as readonly string[]).includes(key)) return key as AdPlatform;
  // An own-property lookup: `constructor`, `toString` or `__proto__` must not resolve through the prototype chain.
  return hasOwn(AD_CHANNEL_ALIASES, key) ? (AD_CHANNEL_ALIASES[key] ?? null) : null;
}

/** True when `key` is an own property of `obj` (never one inherited from `Object.prototype`). */
function hasOwn(obj: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * Whether a discovery connector exists for the platform. Only Meta today; an
 * unsupported platform reports `unsupported` and the UI leaves it out. Adding a
 * platform = flip the flag + a registry entry, mapper and fixtures in the BFF.
 */
export const AD_PLATFORM_SUPPORTED: Record<AdPlatform, boolean> = {
  meta: true,
  tiktok: false,
  google: false,
  youtube: false,
  pinterest: false,
  snapchat: false,
  amazon: false,
};

/** Whether `platform` (any string) has a discovery connector; an own-property lookup, so `constructor` is not one. */
export function isAdPlatformSupported(platform: string): boolean {
  return hasOwn(AD_PLATFORM_SUPPORTED, platform) && AD_PLATFORM_SUPPORTED[platform as AdPlatform] === true;
}

/**
 * Whether an ad-analysis job exists for the platform. Only Meta today: marketing-core's brand ad profile is
 * Meta-only (`scope.platform: 'meta'`). Adding a platform = flip the flag once marketing-core can profile it.
 */
export const AD_ANALYSIS_SUPPORTED: Record<AdPlatform, boolean> = {
  meta: true,
  tiktok: false,
  google: false,
  youtube: false,
  pinterest: false,
  snapchat: false,
  amazon: false,
};

/** Whether `platform` (any string) has an ad-analysis job; an own-property lookup, so `constructor` is not one. */
export function isAdAnalysisSupported(platform: string): boolean {
  return hasOwn(AD_ANALYSIS_SUPPORTED, platform) && AD_ANALYSIS_SUPPORTED[platform as AdPlatform] === true;
}

/* -------------------------------- Jobs ---------------------------------- */

export type AdDiscoveryJobKind = `adDiscovery:${AdPlatform}`;
export const AD_DISCOVERY_JOB_KINDS: AdDiscoveryJobKind[] = AD_PLATFORMS.map(
  (p) => `adDiscovery:${p}` as AdDiscoveryJobKind,
);

/**
 * One ad-analysis job per platform: marketing-core's brand ad profile of the target account (the confirmed
 * account once #15 is answered, else the dominant advertiser). Its `ref` is that account id.
 */
export type AdAnalysisJobKind = `adAnalysis:${AdPlatform}`;
export const AD_ANALYSIS_JOB_KINDS: AdAnalysisJobKind[] = AD_PLATFORMS.map(
  (p) => `adAnalysis:${p}` as AdAnalysisJobKind,
);

export const STATIC_JOB_KINDS = ['siteProfile', 'research', 'competitors', 'funnel'] as const;
export type StaticJobKind = (typeof STATIC_JOB_KINDS)[number];

export type JobKind = StaticJobKind | AdDiscoveryJobKind | AdAnalysisJobKind;
export const JOB_KINDS: JobKind[] = [...STATIC_JOB_KINDS, ...AD_DISCOVERY_JOB_KINDS, ...AD_ANALYSIS_JOB_KINDS];
export const JobKindSchema = z.enum(JOB_KINDS as [JobKind, ...JobKind[]]);

export function adDiscoveryJobKind(platform: AdPlatform): AdDiscoveryJobKind {
  return `adDiscovery:${platform}`;
}

export function adAnalysisJobKind(platform: AdPlatform): AdAnalysisJobKind {
  return `adAnalysis:${platform}`;
}

/** The platform of an `adDiscovery:*` job kind, or null for any other kind. */
export function adPlatformOfJobKind(kind: JobKind): AdPlatform | null {
  return kind.startsWith('adDiscovery:') ? (kind.slice('adDiscovery:'.length) as AdPlatform) : null;
}

/** The platform of an `adAnalysis:*` job kind, or null for any other kind. */
export function adAnalysisPlatformOfJobKind(kind: JobKind): AdPlatform | null {
  return kind.startsWith('adAnalysis:') ? (kind.slice('adAnalysis:'.length) as AdPlatform) : null;
}

/** Explicit step states. A failing job never blocks the quiz. */
export const STEP_STATES = ['queued', 'running', 'done', 'partial', 'unavailable', 'skipped'] as const;
export const StepStateSchema = z.enum(STEP_STATES);
export type StepState = z.infer<typeof StepStateSchema>;

/* ------------------------------- Errors --------------------------------- */

export const ONBOARDING_ERROR_CODES = [
  /** The URL is a social or marketplace page, not the store's own site. */
  'not_a_store',
  /** The URL is not a public website address. */
  'invalid_url',
  'session_not_found',
  'unsupported_platform',
  'competitor_icon_not_found',
  'upstream_unavailable',
  /** 429: too many session creations in the window, or too many live sessions for the operator. `Retry-After` is set. */
  'rate_limited',
  /** 409: a session id that already exists was re-sent with a different site. */
  'session_url_mismatch',
  /** 400: an ad account id that was not among the accounts discovered for that platform. */
  'unknown_ad_account',
  /** 400: a competitor brand id that was not in the research shortlist. */
  'unknown_competitor',
  /** 503: tenant / infra provisioning is switched off (`ONBOARDING_PROVISIONING_ENABLED` is not `true`). */
  'provisioning_disabled',
] as const;
export const OnboardingErrorCodeSchema = z.enum(ONBOARDING_ERROR_CODES);
export type OnboardingErrorCode = z.infer<typeof OnboardingErrorCodeSchema>;

/** `details` of a `not_a_store` error: where the brand lives, so the chat can say so. */
export const NotAStoreDetailsSchema = z.object({
  platform: z.string(),
  label: z.string(),
  handle: z.string().optional(),
  marketplace: z.boolean(),
});
export type NotAStoreDetails = z.infer<typeof NotAStoreDetailsSchema>;

/* ------------------------------ Snapshot -------------------------------- */

const IsoDate = z.string();

export const JobStatusSchema = z.object({
  kind: JobKindSchema,
  state: StepStateSchema,
  /** Stable code of the failure when `state` is `unavailable` / `partial`. */
  errorCode: z.string().optional(),
  updatedAt: IsoDate,
});
export type JobStatus = z.infer<typeof JobStatusSchema>;

/** Most stages / jobs the BFF lists in `researchProgress` (CI has about a dozen of each; this is a bound, not a budget). */
export const MAX_RESEARCH_STAGES = 30;
export const MAX_RESEARCH_JOBS = 60;
/** Longest stage / job token returned (CI tokens are short snake_case such as `market_research_analysis`; clamped, never rejected). */
export const MAX_RESEARCH_TOKEN_LENGTH = 64;

/** One CI stage or job: the RAW upstream token (the client humanizes it) and its step state. */
export const ResearchProgressItemSchema = z.object({
  name: z.string().min(1).max(MAX_RESEARCH_TOKEN_LENGTH),
  state: StepStateSchema,
});
export type ResearchProgressItem = z.infer<typeof ResearchProgressItemSchema>;

/** A CI job: its raw job type, the raw token of the stage it belongs to (the first stage that lists it), and that stage's state. */
export const ResearchProgressJobSchema = ResearchProgressItemSchema.extend({
  stage: z.string().min(1).max(MAX_RESEARCH_TOKEN_LENGTH),
});
export type ResearchProgressJob = z.infer<typeof ResearchProgressJobSchema>;

/**
 * CI workflow progress, when a workflow exists. The five counters are always present. `stages` and `jobs`
 * (phase 2, additive, so an older BFF or fake still type-checks) come from the same CI `/progress` payload:
 *  - `stages`: one entry per CI stage (`ground_truth`, `research`, `creative`, ...), in CI's order. CI's stage
 *    status maps `pending` -> `queued`, `running` -> `running`, `completed` -> `done`, `failed` -> `unavailable`,
 *    `skipped` -> `skipped`.
 *  - `jobs`: one entry per CI job type of those stages (`market_research_analysis`, ...), in stage order, each
 *    once, with `stage` = the raw token of the first stage that lists it (so a client can group jobs by stage). CI's progress payload carries NO per-job status, so a job's `state` is its STAGE's state: in a
 *    running stage with two jobs both read `running` until the stage completes.
 * Both lists are bounded and their names clamped; names are raw CI tokens, never display text.
 */
export const ResearchProgressSchema = z.object({
  totalJobs: z.number().int().min(0),
  completedJobs: z.number().int().min(0),
  runningJobs: z.number().int().min(0),
  failedJobs: z.number().int().min(0),
  queuedJobs: z.number().int().min(0),
  stages: z.array(ResearchProgressItemSchema).max(MAX_RESEARCH_STAGES).optional(),
  jobs: z.array(ResearchProgressJobSchema).max(MAX_RESEARCH_JOBS).optional(),
});
export type ResearchProgress = z.infer<typeof ResearchProgressSchema>;

/**
 * The single-row research card. Each part is independent: `null` means "not
 * read (yet)" and the job state says whether that is spinner or "couldn't read".
 */
export const ResearchSummarySchema = z.object({
  name: z.string().nullable(),
  category: z.string().nullable(),
  productCount: z.number().int().min(0).nullable(),
  liveAdCount: z.number().int().min(0).nullable(),
});
export type ResearchSummary = z.infer<typeof ResearchSummarySchema>;

/** A platform's confirmed accounts, or the client chose to skip it. */
export const AdAccountsConfirmationSchema = z.object({
  skipped: z.boolean(),
  accountIds: z.array(z.string().max(MAX_ID_LENGTH)).max(MAX_AD_ACCOUNT_IDS),
});
export type AdAccountsConfirmation = z.infer<typeof AdAccountsConfirmationSchema>;

export const CompetitorsConfirmationSchema = z.object({
  skipped: z.boolean(),
  brandIds: z.array(z.string().max(MAX_ID_LENGTH)).max(MAX_COMPETITOR_PICKS),
});
export type CompetitorsConfirmation = z.infer<typeof CompetitorsConfirmationSchema>;

export const SessionSnapshotSchema = z.object({
  sessionId: SessionIdSchema,
  /** The URL as given. */
  url: z.string(),
  /** The normalized bare host the jobs run on. */
  domain: z.string(),
  createdAt: IsoDate,
  jobs: z.array(JobStatusSchema),
  research: ResearchSummarySchema,
  /** Absent until the research workflow exists. */
  researchProgress: ResearchProgressSchema.optional(),
  /** True once the quiz baseline was stored. */
  quizSubmitted: z.boolean(),
  confirmations: z.object({
    adAccounts: z.record(AdPlatformSchema, AdAccountsConfirmationSchema),
    competitors: CompetitorsConfirmationSchema.optional(),
  }),
});
export type SessionSnapshot = z.infer<typeof SessionSnapshotSchema>;

/* ------------------------- PUT/GET /sessions/:id ------------------------- */

export const PutSessionRequestSchema = z.object({
  /** A domain or URL as typed; the BFF normalizes it. */
  url: z.string().trim().min(1).max(MAX_URL_LENGTH),
});
export type PutSessionRequest = z.infer<typeof PutSessionRequestSchema>;

export const PutSessionResponseSchema = SessionSnapshotSchema;
export type PutSessionResponse = SessionSnapshot;

export const GetSessionResponseSchema = SessionSnapshotSchema;
export type GetSessionResponse = SessionSnapshot;

/* ---------------------- BrandProfileInput (the quiz) --------------------- */

/**
 * The questionnaire's answers as typed brand-profile input (port of template-project
 * `onboarding-v2/profile-input.ts`, shape only). A skipped question or "Prefer
 * not to say" leaves the field out: absent, never a guessed default.
 */

/** A money range in USD; `max` is absent for an open-ended top bucket. */
export const UsdRangeSchema = z
  .object({
    min: z.number().min(0).max(MAX_USD_AMOUNT),
    max: z.number().min(0).max(MAX_USD_AMOUNT).optional(),
  })
  .refine((r) => r.max === undefined || r.min <= r.max, { message: 'min must not exceed max' });
export type UsdRange = z.infer<typeof UsdRangeSchema>;

export const AdSpendTierSchema = z.enum(['minimal', 'light', 'moderate', 'heavy', 'aggressive']);
export type AdSpendTier = z.infer<typeof AdSpendTierSchema>;

export const GrowthStageSchema = z.enum(['startup', 'early_growth', 'scaling', 'established', 'mature']);
export type GrowthStage = z.infer<typeof GrowthStageSchema>;

export const ChallengeKeySchema = z.enum([
  'cac_rising',
  'losing_share',
  'cant_scale',
  'no_creative_direction',
  'other',
]);
export type ChallengeKey = z.infer<typeof ChallengeKeySchema>;

/** A channel's place in the mix; `share` is a 0-1 fraction range of total spend. */
export const ChannelShareSchema = z.object({
  channel: z.string().min(1).max(MAX_SHORT_TEXT_LENGTH),
  rank: z.number().int().min(1).max(MAX_CHANNELS),
  share: z
    .object({ min: z.number().min(0).max(1), max: z.number().min(0).max(1) })
    .refine((s) => s.min <= s.max, { message: 'share.min must not exceed share.max' })
    .optional(),
});
export type ChannelShare = z.infer<typeof ChannelShareSchema>;

/**
 * Every field is optional: a skipped question or "Prefer not to say" must leave its
 * field ABSENT (never `false`, `[]` or a guess), so "did not answer" and "answered no"
 * stay distinguishable. Present means the person answered.
 */
export const BrandProfileInputSchema = z.object({
  annualRevenueUsd: UsdRangeSchema.optional(),
  growthStage: GrowthStageSchema.optional(),
  /** Absent when the question was skipped; `false` only when the person said they run no paid ads. */
  runsPaidAds: z.boolean().optional(),
  monthlyAdSpendUsd: UsdRangeSchema.optional(),
  spendTier: z.string().max(MAX_SHORT_TEXT_LENGTH).optional(),
  adSpendTier: AdSpendTierSchema.optional(),
  /** Ranked, biggest first. Absent when skipped. */
  channelMix: z.array(ChannelShareSchema).max(MAX_CHANNELS).optional(),
  team: z.string().max(MAX_SHORT_TEXT_LENGTH).optional(),
  challengeKey: ChallengeKeySchema.optional(),
  challengeRaw: z.string().max(MAX_LONG_TEXT_LENGTH).optional(),
  /** Every pain, ranked. Absent when skipped. */
  pains: z.array(z.string().max(MAX_SHORT_TEXT_LENGTH)).max(MAX_PAINS).optional(),
  averageOrderValueUsd: UsdRangeSchema.optional(),
  hasPostPurchaseUpsell: z.boolean().optional(),
  sellsSubscriptions: z.boolean().optional(),
  oneTimePurchase: z.boolean().optional(),
  subscriptionModel: z.enum(['replenishment', 'box', 'service']).optional(),
});
export type BrandProfileInput = z.infer<typeof BrandProfileInputSchema>;

/* ------------------------- POST /sessions/:id/quiz ----------------------- */

export const PostQuizRequestSchema = z.object({
  answers: BrandProfileInputSchema,
});
export type PostQuizRequest = z.infer<typeof PostQuizRequestSchema>;

export const PostQuizResponseSchema = z.object({
  stored: z.literal(true),
});
export type PostQuizResponse = z.infer<typeof PostQuizResponseSchema>;

/* ----------------------- GET /sessions/:id/brand-guide ------------------- */

/** Where a brand-guide field came from. A blank or 404 draft is `missing`. */
export const FIELD_SOURCES = ['site', 'ci', 'missing'] as const;
export const FieldSourceSchema = z.enum(FIELD_SOURCES);
export type FieldSource = z.infer<typeof FieldSourceSchema>;

const sourced = <T extends z.ZodTypeAny>(value: T) =>
  z.object({ value: value.nullable(), source: FieldSourceSchema });

/** A best seller of the site read (`products`): the site's own listing. */
export const BrandGuideProductSchema = z.object({
  name: z.string(),
  imageUrl: z.string(),
  url: z.string().optional(),
  price: z.string().optional(),
});
export type BrandGuideProduct = z.infer<typeof BrandGuideProductSchema>;

/** One tone word CI read from the site, with the text it based it on. */
export const BrandGuideToneSchema = z.object({
  adjective: z.string(),
  /** The site text the word rests on; null when the upstream gave none. */
  evidence: z.string().nullable(),
});
export type BrandGuideTone = z.infer<typeof BrandGuideToneSchema>;

export const BrandGuideDifferentiatorSchema = z.object({
  claim: z.string(),
  evidence: z.string().nullable(),
  /** Null when the upstream did not say. */
  isDefensible: z.boolean().nullable(),
});
export type BrandGuideDifferentiator = z.infer<typeof BrandGuideDifferentiatorSchema>;

/** A top seller as CI's research found it (not the site's own best-sellers list, which is `products`). */
export const BrandGuideTopSellerSchema = z.object({
  name: z.string(),
  price: z.string().nullable(),
  currency: z.string().nullable(),
  url: z.string().nullable(),
  imageUrl: z.string().nullable(),
  whyWinning: z.string().nullable(),
  isStar: z.boolean(),
});
export type BrandGuideTopSeller = z.infer<typeof BrandGuideTopSellerSchema>;

/**
 * The voice of the Brand Context draft (`dna.voice`). Only the non-blank entries are present, and only
 * once the draft was written (it has a `createdAt`): the API answers a blank template before that.
 */
export const BrandGuideVoiceSchema = z.object({
  positioningMore: z.string().optional(),
  positioningLess: z.string().optional(),
  wordsWeUse: z.string().optional(),
  wordsWeAvoid: z.string().optional(),
  associateWith: z.string().optional(),
  dontAssociateWith: z.string().optional(),
  taglines: z.string().optional(),
  guidelinesNote: z.string().optional(),
});
export type BrandGuideVoice = z.infer<typeof BrandGuideVoiceSchema>;

export const PRODUCTS_SOURCES = ['best-sellers', 'catalog'] as const;
export type ProductsSource = (typeof PRODUCTS_SOURCES)[number];

export const BrandGuideResponseSchema = z.object({
  name: sourced(z.string()),
  description: sourced(z.string()),
  /** The brand's logo: JSON-LD `Organization.logo` of its site. `missing` when the site declares none (never `og:image`). */
  logoUrl: sourced(z.string()),
  /** The site's icon: apple-touch-icon, else the largest `<link rel=icon>`. */
  iconUrl: sourced(z.string()),
  /** The site's `og:image` (the "Site image" field). Not a logo. Optional so an older client or fake still type-checks. */
  siteImageUrl: sourced(z.string()).optional(),
  fonts: sourced(z.array(z.string())),
  /** Hex values as found on the site, most used first. No role names. */
  colors: sourced(z.array(z.string())),
  /** No source exists for these today; always `missing`. */
  languageVersions: sourced(z.array(z.string())),
  marketVersions: sourced(z.array(z.string())),
  /*
   * Phase 2 (additive). Optional so an older client or fake still type-checks; the BFF always sends all
   * of them, each with a source. A field CI could not read is `{value: null, source: 'missing'}`.
   */
  /** `site`: the site's own best sellers. */
  products: sourced(z.array(BrandGuideProductSchema)).optional(),
  /**
   * Where `products` came from: the site's best-sellers collection, or its catalog when it has none.
   * `null` when no products were read. Optional so an older client or fake still type-checks.
   */
  productsSource: z.enum(PRODUCTS_SOURCES).nullable().optional(),
  /** `ci`: the brand profile tagline. */
  tagline: sourced(z.string()).optional(),
  /** `ci`: positioning statement. */
  positioning: sourced(z.string()).optional(),
  /** `ci`: what the brand says about itself. */
  about: sourced(z.string()).optional(),
  /** `ci`: the taxonomy category (for example "Shapewear"). */
  category: sourced(z.string()).optional(),
  /** `ci`: the upstream price-tier token (`premium`, `mid_market`, `value`, ...). Not display text. */
  priceTier: sourced(z.string()).optional(),
  /** `ci`: tone words with evidence. */
  tone: sourced(z.array(BrandGuideToneSchema)).optional(),
  /** `ci`: the ideal customer profile. */
  icp: sourced(z.string()).optional(),
  differentiators: sourced(z.array(BrandGuideDifferentiatorSchema)).optional(),
  topSellers: sourced(z.array(BrandGuideTopSellerSchema)).optional(),
  /** `ci`: the Brand Context draft voice; `missing` until the draft was written. */
  voice: sourced(BrandGuideVoiceSchema).optional(),
});
export type BrandGuideResponse = z.infer<typeof BrandGuideResponseSchema>;

/* ------------------------ ad accounts (confirm #15) ---------------------- */

export const AdAccountSchema = z.object({
  accountId: z.string(),
  name: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  adCount: z.number().int().min(0),
});
export type AdAccount = z.infer<typeof AdAccountSchema>;

export const AdPlatformStatusSchema = z.union([StepStateSchema, z.literal('unsupported')]);
export type AdPlatformStatus = z.infer<typeof AdPlatformStatusSchema>;

export const AdAccountsPlatformSchema = z.object({
  platform: AdPlatformSchema,
  status: AdPlatformStatusSchema,
  /** Distinct advertisers in that platform's discovery events. */
  accounts: z.array(AdAccountSchema).max(MAX_RANKED_ITEMS),
  confirmation: AdAccountsConfirmationSchema.optional(),
});
export type AdAccountsPlatform = z.infer<typeof AdAccountsPlatformSchema>;

export const GetAdAccountsResponseSchema = z.object({
  platforms: z.array(AdAccountsPlatformSchema),
});
export type GetAdAccountsResponse = z.infer<typeof GetAdAccountsResponseSchema>;

export const PutAdAccountsRequestSchema = z.union([
  z.object({ accountIds: z.array(z.string().min(1).max(MAX_ID_LENGTH)).min(1).max(MAX_AD_ACCOUNT_IDS) }).strict(),
  z.object({ skipped: z.literal(true) }).strict(),
]);
export type PutAdAccountsRequest = z.infer<typeof PutAdAccountsRequestSchema>;

export const PutAdAccountsResponseSchema = z.object({
  platform: AdPlatformSchema,
  confirmation: AdAccountsConfirmationSchema,
});
export type PutAdAccountsResponse = z.infer<typeof PutAdAccountsResponseSchema>;

/* ------------------- ads events (platform-neutral envelope) -------------- */

/** One ad as the discovery stream reports it, with platform terms stripped. */
export const NeutralAdSchema = z.object({
  adId: z.string().min(1).max(MAX_ID_LENGTH),
  /** Order in which the ad was first discovered (dedupe key with `revision`). */
  ordinal: z.number().int().min(1),
  revision: z.number().int().min(1),
  // Third-party values: the BFF mapper cleans (no control or bidi characters) and clamps every one of these, so the
  // bounds are guarantees, not hopes.
  advertiser: z.object({
    accountId: z.string().max(MAX_ID_LENGTH).optional(),
    name: z.string().max(MAX_SITE_NAME_LENGTH).optional(),
    avatarUrl: z.string().max(MAX_URL_LENGTH).optional(),
  }),
  title: z.string().max(MAX_SITE_NAME_LENGTH).optional(),
  copy: z.string().max(MAX_SITE_DESCRIPTION_LENGTH).optional(),
  mediaType: z.string().max(MAX_SHORT_TEXT_LENGTH).optional(),
  thumbnailUrl: z.string().max(MAX_URL_LENGTH).optional(),
  videoUrl: z.string().max(MAX_URL_LENGTH).optional(),
  /** The platform's own page for the ad (e.g. the Meta Ad Library entry). */
  adUrl: z.string().max(MAX_URL_LENGTH).optional(),
  landingUrl: z.string().max(MAX_URL_LENGTH).optional(),
  daysRunning: z.number().int().min(0).optional(),
});
export type NeutralAd = z.infer<typeof NeutralAdSchema>;

export const NeutralTerminalSchema = z.object({
  status: z.string(),
  hasUsableResults: z.boolean(),
  accepted: z.number().int().min(0),
  rejected: z.number().int().min(0),
  duplicateCreatives: z.number().int().min(0),
  warningCodes: z.array(z.string()).max(MAX_RANKED_ITEMS),
});
export type NeutralTerminal = z.infer<typeof NeutralTerminalSchema>;

const neutralEvent = <T extends string, D extends z.ZodTypeAny>(type: T, data: D) =>
  z.object({
    sequence: z.number().int().min(0),
    type: z.literal(type),
    data,
  });

export const NeutralAdEventSchema = z.discriminatedUnion('type', [
  neutralEvent('queued', z.object({})),
  neutralEvent('started', z.object({})),
  neutralEvent('stage_changed', z.object({ stage: z.string() })),
  neutralEvent('ad_discovered', NeutralAdSchema),
  neutralEvent('ad_updated', NeutralAdSchema),
  neutralEvent('warning', z.object({ code: z.string(), message: z.string().optional() })),
  neutralEvent('completed', NeutralTerminalSchema),
  neutralEvent('failed', NeutralTerminalSchema),
]);
export type NeutralAdEvent = z.infer<typeof NeutralAdEventSchema>;

export const GetAdEventsQuerySchema = z.object({
  /** Resume cursor: the `nextSince` of the previous response. */
  since: z.coerce.number().int().min(0).max(MAX_EVENT_CURSOR).optional(),
});
export type GetAdEventsQuery = z.infer<typeof GetAdEventsQuerySchema>;

export const GetAdEventsResponseSchema = z.object({
  platform: AdPlatformSchema,
  status: StepStateSchema,
  events: z.array(NeutralAdEventSchema),
  nextSince: z.number().int().min(0),
  lastSequence: z.number().int().min(0),
  /** True once the collection will emit nothing more. */
  terminal: z.boolean(),
  /** True when the upstream stream restarted: the client drops its cursor and state. */
  reset: z.boolean().optional(),
});
export type GetAdEventsResponse = z.infer<typeof GetAdEventsResponseSchema>;

/* ------------------- ad analysis (brand ad profile, Scout) --------------- */

/** Most entries of each pattern list (funnel stages, hooks, styles, formats) and of `topAds`. */
export const MAX_ANALYSIS_ITEMS = 10;
export const MAX_TOP_ADS = 10;
/** Longest pattern label returned (marketing-core vocabulary tokens; clamped, never rejected). */
export const MAX_ANALYSIS_LABEL_LENGTH = 80;

/** One pattern of the profile and how many analyzed ads show it. `name` is the upstream token, lowercase; the client humanizes it. */
export const AnalysisCountSchema = z.object({
  name: z.string().min(1).max(MAX_ANALYSIS_LABEL_LENGTH),
  count: z.number().int().min(0),
});
export type AnalysisCount = z.infer<typeof AnalysisCountSchema>;

/** A top ad by days running: only what the card shows. Title and copy are clamped. */
export const TopAdSchema = z.object({
  adId: z.string().min(1).max(MAX_ID_LENGTH),
  title: z.string().max(MAX_SITE_NAME_LENGTH).optional(),
  copy: z.string().max(MAX_SITE_DESCRIPTION_LENGTH).optional(),
  mediaType: z.string().max(MAX_SHORT_TEXT_LENGTH).optional(),
  thumbnailUrl: z.string().max(MAX_URL_LENGTH).optional(),
  adUrl: z.string().max(MAX_URL_LENGTH).optional(),
  daysRunning: z.number().int().min(0),
});
export type TopAd = z.infer<typeof TopAdSchema>;

/**
 * The brand ad profile of one platform's target account: marketing-core's strategy profile, projected to what
 * the Scout card shows. There are NO per-ad scores and no "creative health" fields: nothing upstream has them.
 *  - `state`: the `adAnalysis:{platform}` job. `queued` before the job exists (ads still being found), `skipped`
 *    only once discovery is known to have ended with no ad that has media (or no advertiser id), so it is final,
 *    `running`, `done` (profile ready), `partial` (the numbers so far; it can still become `done`, except with
 *    `analysis_complete`), `unavailable`.
 *  - `accountId`: the account the numbers belong to; null until the job exists.
 *  - `coverage.analyzed` / `analyzable`: marketing-core `coverage.analyzedAds` / `analyzableAds`; `analyzed` is cut
 *    to `analyzable` (marketing-core's raw `analyzedAds` also counts analyzed ads that are no longer analyzable).
 *  - `mediaMix`: the ads of the account by media type (`video`, and `image` as `static`); other media types only show in `formats`.
 *  - `funnelStages`, `topHooks`, `topStyles`, `formats`: counts, biggest first, bounded; empty until an analysis exists.
 *  - `longRunningAdRate`: share (0..1) of the account's ads running 30+ days; null until the profile was read.
 *  - `avgDaysRunning`: null when no ad reported days running.
 *  - `topAds`: the account's ads with the most days running, from the discovery stream (no upstream sorts by it).
 */
export const AdAnalysisSummarySchema = z.object({
  platform: AdPlatformSchema,
  accountId: z.string().max(MAX_ID_LENGTH).nullable(),
  state: StepStateSchema,
  /**
   * Stable code when `state` is `unavailable` / `partial`: `analysis_stalled`, `analysis_incomplete`,
   * `analysis_complete`, `insufficient_data`, `no_rows`, `ambiguous_identity`, `start_failed`,
   * `start_interrupted`, `upstream_unavailable`.
   */
  errorCode: z.string().optional(),
  /**
   * True when the BFF will not move the job any more: `done`, `unavailable`, `skipped`, or `partial` and settled
   * (`analysis_complete`, or past `AD_ANALYSIS_POLL_HORIZON_MS` from the job's start). While the job is `partial`
   * and not `final` a late `ready` can still make it `done`, so a client may keep polling slowly.
   */
  final: z.boolean(),
  coverage: z.object({ analyzed: z.number().int().min(0), analyzable: z.number().int().min(0) }),
  mediaMix: z.object({ video: z.number().int().min(0), static: z.number().int().min(0) }),
  funnelStages: z.array(AnalysisCountSchema).max(MAX_ANALYSIS_ITEMS),
  topHooks: z.array(AnalysisCountSchema).max(MAX_ANALYSIS_ITEMS),
  topStyles: z.array(AnalysisCountSchema).max(MAX_ANALYSIS_ITEMS),
  formats: z.array(AnalysisCountSchema).max(MAX_ANALYSIS_ITEMS),
  longRunningAdRate: z.number().min(0).max(1).nullable(),
  avgDaysRunning: z.number().min(0).nullable(),
  topAds: z.array(TopAdSchema).max(MAX_TOP_ADS),
});
export type AdAnalysisSummary = z.infer<typeof AdAnalysisSummarySchema>;

export const GetAdAnalysisResponseSchema = AdAnalysisSummarySchema;
export type GetAdAnalysisResponse = AdAnalysisSummary;

/* ----------------------------- competitors (#16) ------------------------- */

/**
 * A competitor from the CI `live_competitors` artifact. The upstream items
 * carry no id. `brandId` is therefore derived by the BFF adapter (the normalized
 * domain, else a slug of the name) and is what `PUT /competitors { brandIds }` confirms.
 * The other fields come from the item's `taxonomy` and `adData` and are ABSENT when the
 * upstream did not send them (the playbook-cluster fallback carries only `similarity`).
 */
export const CompetitorSchema = z.object({
  /** At most `MAX_ID_LENGTH`: a domain or a slug that fits, else a stable `h-<16 hex>` hash (the PUT accepts the same bound). */
  brandId: z.string().min(1).max(MAX_ID_LENGTH),
  name: z.string().max(MAX_SITE_NAME_LENGTH),
  domain: z.string().max(MAX_ID_LENGTH).nullable(),
  /** 0..1, how close the brand is to the prospect (CI `taxonomy.similarity`). */
  similarity: z.number().min(0).max(1).optional(),
  /** Ads tracked for the brand (CI `adData.adCount`). */
  adCount: z.number().int().min(0).optional(),
  /** The format most of its ads use, for example `video` or `static` (CI `adData.dominantFormat`). */
  dominantFormat: z.string().max(MAX_SHORT_TEXT_LENGTH).optional(),
  /** CI's vertical for the brand (`taxonomy.vertical`). */
  vertical: z.string().max(MAX_SHORT_TEXT_LENGTH).optional(),
  /**
   * True when the row comes from CI's own reference catalog of tracked brands (the UI says "from our
   * catalog"); false for the research-cluster fallback. Absent only in older payloads.
   */
  fromCatalog: z.boolean().optional(),
  /**
   * The brand's logo (CI `avatarUrl`), an https URL on a public host (checked by the BFF). Absent when
   * CI has none (always for the fallback rows): the UI shows the initial.
   */
  imageUrl: z.string().max(MAX_URL_LENGTH).optional(),
  /** Why the live search named the brand, in one sentence (CI `reason`). */
  reason: z.string().max(MAX_COMPETITOR_REASON_LENGTH).optional(),
});
export type Competitor = z.infer<typeof CompetitorSchema>;

export const GetCompetitorsResponseSchema = z.object({
  /** `unavailable` / empty list is the empty state (workflow failed or niche outside the catalog). */
  status: StepStateSchema,
  /** At most `MAX_RANKED_ITEMS` rows. */
  competitors: z.array(CompetitorSchema).max(MAX_RANKED_ITEMS),
  confirmation: CompetitorsConfirmationSchema.optional(),
});
export type GetCompetitorsResponse = z.infer<typeof GetCompetitorsResponseSchema>;

export const PutCompetitorsRequestSchema = z.union([
  z.object({ brandIds: z.array(z.string().min(1).max(MAX_ID_LENGTH)).min(1).max(MAX_COMPETITOR_PICKS) }).strict(),
  z.object({ skipped: z.literal(true) }).strict(),
]);
export type PutCompetitorsRequest = z.infer<typeof PutCompetitorsRequestSchema>;

export const PutCompetitorsResponseSchema = z.object({
  confirmation: CompetitorsConfirmationSchema,
});
export type PutCompetitorsResponse = z.infer<typeof PutCompetitorsResponseSchema>;
